import { useEffect, useState } from "react";
import { Wallet as WalletIcon, ShieldCheck, ArrowDownToLine, CalendarCheck, Loader2, Gift } from "lucide-react";
import { Link } from "react-router-dom";
import { api, brl, apiError } from "@/lib/api";
import { TopBar, NotificationBell } from "@/components/Shell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const STATUS_COLOR = { pendente: "#F5C518", aprovado: "#2E8B57", recusado: "#E53238" };

export default function WalletPage() {
  const [w, setW] = useState(null);
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const [kycForm, setKycForm] = useState({
    full_name: "", cpf: "", birth_date: "", pix_type: "cpf", pix_key: "",
  });

  const load = () => api.get("/wallet").then(({ data }) => setW(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const submitKyc = async () => {
    setBusy(true);
    try {
      await api.post("/wallet/kyc", kycForm);
      toast.success("KYC enviado! Aguarde a validação da Time Food.");
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const withdraw = async () => {
    setBusy(true);
    try {
      await api.post("/wallet/withdrawals", { amount: parseFloat(amount) || 0 });
      toast.success("Saque solicitado! A Time Food vai analisar.");
      setAmount("");
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  if (!w) return <div className="p-6 text-sm text-gray-500">Carregando carteira…</div>;

  const kycOk = w.kyc?.status === "aprovado";
  const fee = (parseFloat(amount) || 0) * (w.today_fee_pct / 100);

  return (
    <div data-testid="wallet-page" className="pb-8">
      <TopBar subtitle="Minha carteira" right={<NotificationBell />} />

      <div className="px-4 pt-5">
        <div className="rounded-3xl gf-gradient p-5 text-white" data-testid="wallet-balance-card">
          <p className="text-[11px] uppercase tracking-wider font-bold text-white/80">Saldo disponível</p>
          <h1 className="font-display text-4xl font-extrabold mt-1" data-testid="wallet-balance">{brl(w.balance)}</h1>
          <div className="flex gap-4 mt-2 text-xs text-white/85">
            <span>Recebido: {brl(w.earned)}</span>
            <span>Sacado/em análise: {brl(w.withdrawn)}</span>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2 mt-3">
          {w.by_kind.map((k) => (
            <div key={k.kind} className="rounded-2xl border border-gray-200 p-3" data-testid={`wallet-kind-${k.kind}`}>
              <p className="text-[11px] text-gray-500">{k.label}</p>
              <p className="font-display font-extrabold">{brl(k.total)}</p>
            </div>
          ))}
        </div>

        <Link
          to="/indique"
          data-testid="wallet-referral-cta"
          className="mt-3 flex items-center gap-3 rounded-2xl border-2 border-dashed border-gf-orange p-4 bg-orange-50 gf-press"
        >
          <Gift size={20} className="text-gf-orange" />
          <div>
            <p className="font-display font-extrabold text-sm">Ganhe até 3,5% + 2,5% para sempre</p>
            <p className="text-[11px] text-gray-600">Compartilhe seu link e receba de tudo que seus indicados consumirem.</p>
          </div>
        </Link>

        {/* Regras de saque */}
        <div className="mt-6 rounded-2xl border border-gray-200 p-4" data-testid="withdraw-rules">
          <h2 className="font-display font-bold flex items-center gap-2 text-base">
            <CalendarCheck size={17} className="text-mage-green" /> Regras de saque
          </h2>
          <ul className="mt-2 text-xs text-gray-600 space-y-1">
            <li>• <b>Terça-feira</b>: saque <b className="text-mage-green">gratuito</b> (1 por semana)</li>
            <li>• <b>Sexta-feira</b>: taxa de <b>1%</b> sobre o valor</li>
            <li>• Outros dias: taxa de <b>5%</b> sobre o valor</li>
          </ul>
          <p className="text-[11px] text-gray-500 mt-2">
            Taxa de hoje: <b data-testid="today-fee">{w.today_fee_pct}%</b> · próximo saque gratuito em{" "}
            <b>{new Date(w.next_free_withdraw + "T12:00:00").toLocaleDateString("pt-BR")}</b>
          </p>
        </div>

        {/* KYC */}
        <div className="mt-6" data-testid="kyc-section">
          <h2 className="font-display font-bold flex items-center gap-2 text-base">
            <ShieldCheck size={17} className="text-gf-orange" /> Verificação (KYC) e conta Pix
          </h2>
          {kycOk ? (
            <div className="mt-2 rounded-2xl border-2 border-mage-green p-4 bg-green-50" data-testid="kyc-approved">
              <p className="text-sm font-bold text-mage-green">Verificação aprovada</p>
              <p className="text-xs text-gray-600 mt-1">
                {w.kyc.full_name} · Pix ({w.kyc.pix_type}): <b>{w.kyc.pix_key}</b>
              </p>
            </div>
          ) : w.kyc?.status === "pendente" ? (
            <p className="mt-2 text-sm text-gray-600 rounded-2xl border p-4" data-testid="kyc-pending">
              Seus dados estão em análise pela Time Food. Você será avisado por notificação.
            </p>
          ) : (
            <div className="mt-2 grid gap-2">
              <Input data-testid="kyc-name-input" placeholder="Nome completo" value={kycForm.full_name}
                onChange={(e) => setKycForm({ ...kycForm, full_name: e.target.value })} className="h-11 rounded-xl" />
              <Input data-testid="kyc-cpf-input" placeholder="CPF" value={kycForm.cpf}
                onChange={(e) => setKycForm({ ...kycForm, cpf: e.target.value })} className="h-11 rounded-xl" />
              <div>
                <Label className="text-xs text-gray-500">Data de nascimento (dd/mm/aaaa)</Label>
                <Input data-testid="kyc-birth-input" placeholder="dd/mm/aaaa" inputMode="numeric" maxLength={10}
                  value={kycForm.birth_date}
                  onChange={(e) => {
                    const digits = e.target.value.replace(/\D/g, "").slice(0, 8);
                    const masked = digits.length > 4
                      ? `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`
                      : digits.length > 2 ? `${digits.slice(0, 2)}/${digits.slice(2)}` : digits;
                    setKycForm({ ...kycForm, birth_date: masked });
                  }}
                  className="h-11 rounded-xl mt-1" />
              </div>
              <Select value={kycForm.pix_type} onValueChange={(v) => setKycForm({ ...kycForm, pix_type: v })}>
                <SelectTrigger className="h-11 rounded-xl" data-testid="kyc-pix-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="cpf">Pix CPF</SelectItem>
                  <SelectItem value="email">Pix E-mail</SelectItem>
                  <SelectItem value="telefone">Pix Telefone</SelectItem>
                  <SelectItem value="aleatoria">Pix Chave aleatória</SelectItem>
                </SelectContent>
              </Select>
              <Input data-testid="kyc-pix-input" placeholder="Chave Pix" value={kycForm.pix_key}
                onChange={(e) => setKycForm({ ...kycForm, pix_key: e.target.value })} className="h-11 rounded-xl" />
              <Button data-testid="submit-kyc-button" onClick={submitKyc} disabled={busy}
                className="h-11 rounded-xl gf-gradient text-white font-bold gf-press">
                {busy ? <Loader2 className="animate-spin" size={16} /> : "Enviar verificação"}
              </Button>
            </div>
          )}
        </div>

        {/* Saque */}
        <div className="mt-6" data-testid="withdraw-section">
          <h2 className="font-display font-bold flex items-center gap-2 text-base">
            <ArrowDownToLine size={17} className="text-gf-orange" /> Solicitar saque
          </h2>
          <div className="mt-2 flex gap-2">
            <Input data-testid="withdraw-amount-input" placeholder="Valor (R$)" value={amount}
              onChange={(e) => setAmount(e.target.value)} className="h-11 rounded-xl" />
            <Button data-testid="request-withdraw-button" onClick={withdraw} disabled={busy || !kycOk}
              className="h-11 rounded-xl gf-gradient text-white font-bold gf-press">
              Sacar
            </Button>
          </div>
          {amount && (
            <p className="text-[11px] text-gray-500 mt-1" data-testid="withdraw-preview">
              Taxa de hoje {w.today_fee_pct}% = {brl(fee)} · você recebe {brl((parseFloat(amount) || 0) - fee)}
            </p>
          )}
          {!kycOk && <p className="text-[11px] text-mage-red mt-1">Conclua o KYC para liberar saques.</p>}
        </div>

        <h2 className="font-display font-bold text-base mt-6">Meus saques</h2>
        <div className="grid gap-2 mt-2" data-testid="withdrawals-list">
          {w.withdrawals.map((r) => (
            <div key={r.id} className="rounded-2xl border border-gray-200 p-3 flex justify-between items-center">
              <div>
                <p className="font-semibold text-sm">{brl(r.amount)}</p>
                <p className="text-[11px] text-gray-500">
                  taxa {r.fee_pct}% · líquido {brl(r.net_amount)} · {new Date(r.created_at).toLocaleDateString("pt-BR")}
                </p>
              </div>
              <span className="text-[10px] font-bold px-2 py-1 rounded-full text-white"
                style={{ background: STATUS_COLOR[r.status] }}>
                {r.status.toUpperCase()}
              </span>
            </div>
          ))}
          {w.withdrawals.length === 0 && <p className="text-xs text-gray-500">Nenhum saque solicitado.</p>}
        </div>

        <h2 className="font-display font-bold text-base mt-6">Extrato</h2>
        <div className="grid gap-2 mt-2" data-testid="ledger-list">
          {w.entries.map((e) => (
            <div key={e.id} className="rounded-2xl border border-gray-200 p-3 flex justify-between items-center">
              <div className="min-w-0">
                <p className="font-semibold text-sm truncate">{e.label}</p>
                <p className="text-[11px] text-gray-500 truncate">{e.description}</p>
              </div>
              <p className="font-display font-extrabold text-mage-green shrink-0 ml-2">+ {brl(e.amount)}</p>
            </div>
          ))}
          {w.entries.length === 0 && <p className="text-xs text-gray-500">Sem lançamentos ainda.</p>}
        </div>
      </div>
    </div>
  );
}
